type DeviceData = {
  acceptHeader: string;
  challengeWindowSize: string;
  colorDepth: string;
  ip: string;
  javaEnabled: boolean;
  javascriptEnabled: boolean;
  language: string;
  screenHeight: number;
  screenWidth: number;
  timezone: number;
  userAgent: string;
};

/**
 * Gathers device data for MPP processing
 * @param {string} ipAddress - User's IP address
 * @returns {DeviceData} Device data object
 */
function gatherDeviceData(ipAddress: string): DeviceData {
  const acceptHeader = 'text/html,application/xhtml+xml,application/xml;q=0.9,image/webp,image/apng,*/*;q=0.8';
  const colorDepth = getColorDepth(window.screen.colorDepth);

  const enhancedProcessingData: DeviceData = {
    acceptHeader: acceptHeader,
    challengeWindowSize: 'FULL_SCREEN',
    colorDepth: colorDepth,
    ip: ipAddress,
    javaEnabled: navigator.javaEnabled(),
    javascriptEnabled: true,
    language: navigator.language,
    screenHeight: window.innerHeight,
    screenWidth: window.innerWidth,
    timezone: new Date().getTimezoneOffset() / 60,
    userAgent: navigator.userAgent
  };

  return enhancedProcessingData;
}

type ColorDepthValues = {
  [key: number]: string;
};

/**
 * Maps color depth to API-compatible string
 * @param {number} colorDepth - Screen color depth
 * @returns {string} Color depth string value
 */
function getColorDepth(colorDepth: number): string {
  // these are the allowed values to be passed to the API
  const colorDepthValues: ColorDepthValues = {
    1: 'ONE_BIT',
    2: 'TWO_BITS',
    4: 'FOUR_BITS',
    8: 'EIGHT_BITS',
    15: 'FIFTEEN_BITS',
    16: 'SIXTEEN_BITS',
    24: 'TWENTY_FOUR_BITS',
    32: 'THIRTY_TWO_BITS',
    48: 'FORTY_EIGHT_BITS'
  };

  if (colorDepthValues[colorDepth]) {
    return colorDepthValues[colorDepth] || 'EIGHT_BITS';
  } else {
    // this will calculate the closest lowest match
    const closestDepth = findClosestColorDepth(colorDepthValues, colorDepth);
    return colorDepthValues[closestDepth] ?? 'EIGHT_BITS';
  }
}

/**
 * Finds the closest color depth match
 * @param {ColorDepthValues} obj - Color depth mapping object
 * @param {number} val - Target value
 * @returns {number} Closest matching key
 */
function findClosestColorDepth(obj: ColorDepthValues, val: number): number {
  const keys = Object.keys(obj).map(Number);

  keys.sort((a, b) => {
    return a - b;
  });

  let maxKey = -1;

  for (let i = 0, len = keys.length; i < len; i++) {
    const key = keys[i];
    if (key && (maxKey < 0 || key < val)) {
      maxKey = Math.max(maxKey, key);
    }
  }

  return maxKey;
}

export { gatherDeviceData };
